import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { createGeideaSession, confirmGeideaPayment } from "../lib/api";
import { startGeideaPayment } from "../lib/geidea";
import { toast } from "sonner";

/**
 * Geidea drop-in checkout panel (Egypt flow).
 *
 * Mirrors the embedded experience of <StripeCheckoutPanel/> — the order summary
 * lives in the parent Checkout page; this component renders only the payment
 * widget itself inside a container div on the same page.
 *
 * Flow:
 *   1) Component mounts → calls `POST /api/orders/{orderId}/geidea-session`
 *      which returns a Geidea sessionId.
 *   2) Dynamically loads geideaCheckout.min.js (idempotent — cached after 1st load).
 *   3) Calls `payment.startPayment(sessionId, null, "geidea-checkout-container")`
 *      so Geidea renders inline inside our div (not as a popup).
 *   4) onSuccess: posts to backend `/api/orders/{orderId}/geidea-confirm` so the
 *      order flips to Paid even if the webhook is delayed, then navigates to
 *      /success/{orderId}.
 *   5) onError/onCancel: shows a re-pay button.
 */
export default function GeideaCheckoutPanel({ orderId, onSuccess }) {
  const [status, setStatus] = useState("initializing"); // initializing | ready | error
  const [errorMsg, setErrorMsg] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    startedRef.current = false;

    const bootstrap = async () => {
      setStatus("initializing");
      setErrorMsg(null);
      try {
        const sess = await createGeideaSession(orderId);
        if (cancelled) return;
        // Geidea SDK mounts into `#geidea-checkout-container` (drop-in mode).
        // The container must exist in the DOM before startPayment is called.
        await startGeideaPayment({
          sessionId: sess.session_id,
          containerId: "geidea-checkout-container",
          onSuccess: async (data) => {
            try {
              await confirmGeideaPayment(orderId, {
                responseCode: data?.responseCode,
                orderId: data?.orderId,
                reference: data?.reference,
              });
            } catch (_) { /* webhook will catch up */ }
            if (typeof onSuccess === "function") onSuccess();
          },
          onError: (err) => {
            const msg = err?.detailedResponseMessage || err?.responseMessage || "تعذّر إتمام الدفع، حاول مرة أخرى.";
            setStatus("error");
            setErrorMsg(msg);
            toast.error(msg);
          },
          onCancel: () => {
            setStatus("error");
            setErrorMsg("تم إلغاء الدفع. يمكنك المحاولة مرة أخرى.");
          },
        });
        if (!cancelled) setStatus("ready");
      } catch (e) {
        if (cancelled) return;
        const msg = e?.response?.data?.detail || "تعذّر بدء عملية الدفع، حاول مرة أخرى.";
        setStatus("error");
        setErrorMsg(msg);
      }
    };

    bootstrap();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, retryKey]);

  return (
    <div data-testid="geidea-panel">
      {status === "initializing" && (
        <div className="flex items-center justify-center gap-2 py-10 text-[#A1B0C6]" data-testid="geidea-loading">
          <Loader2 className="w-5 h-5 animate-spin text-[#C5A059]" />
          <span className="text-sm">جاري تجهيز بوابة الدفع...</span>
        </div>
      )}

      {/* The Geidea SDK injects an iframe inside this div in drop-in mode. */}
      <div
        id="geidea-checkout-container"
        className={`w-full ${status === "ready" ? "min-h-[520px]" : "min-h-0"}`}
        data-testid="geidea-checkout-container"
      />

      {status === "error" && (
        <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-sm text-right" dir="rtl">
          <div className="text-red-300 text-sm font-bold mb-3">{errorMsg}</div>
          <button
            onClick={() => setRetryKey((k) => k + 1)}
            data-testid="geidea-retry"
            className="bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] font-bold text-sm px-5 py-2 rounded-sm"
          >
            إعادة المحاولة
          </button>
        </div>
      )}
    </div>
  );
}
